<template>
  <div
    class="top-level-wrapper"
    :class="{ grabbing: isDragging }"
    @mouseup="release"
  >
    <div
      class="home-container"
      :class="{ 'show-results': showResults, hideSidebar: hideSidebar }"
    >
      <nav-header @toggle-sidebar="hideSidebar = !hideSidebar" />
      <div class="main-container">
        <div class="pseudo-sidebar-before" />
        <div class="sidebar-wrapper">
          <vue-scroll>
            <side-bar />
          </vue-scroll>
        </div>
        <div ref="toolWrapper" class="dashboard-tool-wrapper">
          <dashboard v-if="isDashboardMode" />
          <div
            v-else
            style="position: relative; height: 100%; width: 100%;"
            :class="{ extended: plannerExtended }"
         >
            <div  class="map-plan-wrapper" @mousemove="handleDrag">
              <div ref="mepToolMapWrapper" id="mep-tool-wrapper">
                 <!-- listen to mousedown event from resizer so that
                 resizing only happens when user start dragging from the
                 resizer -->
                <div
                  @mousedown="press"
                  id="resizer"
                  title="Click & drag this grabber to resize the map width"
                ></div>
                <mep-tool-map />
              </div>
              <div class="plan-wrapper">
                <div
                  class="width-toggler"
                  @click="plannerExtended = !plannerExtended"
                />
                <router-view />
              </div>
            </div>
          </div>
        </div>
      </div>
      <org-footer v-if="!showResults" />
    </div>
    <div v-if="showResults" class="result-outer-wrapper">
      <div class="divider" />
      <bat-results v-if="showBatResults" />
      <mhp-results v-else-if="showMhpResults" />
      <sst-results v-else-if="showSstResults" />
      <org-footer dark />
    </div>
  </div>
</template>

<script>
import NavHeader from "../../organisms/Home/Header";
import SideBar from "../../organisms/Home/Sidebar";
import Dashboard from "./Dashboard";
import MepToolMap from "./MepToolMap";
import BatResults from "./BatResults";
import SstResults from "./SstResults";
import MhpResults from "./MhpResults";
import Footer from "../../organisms/Home/Footer";

import { bus } from "../../../main";
import { initMap, fitToNepalBounds, getMap } from "../../../js/map";
import { initNavigator, getNavigator } from "../../../js/mapNavigator";
import { mapGetters, mapActions } from "vuex";

export default {
  name: "home",
  components: {
    NavHeader,
    SideBar,
    Dashboard,
    MepToolMap,
    BatResults,
    MhpResults,
    SstResults,
    "org-footer": Footer,
  },
  data() {
    return {
      hideSidebar: false,
      setTargetMapTimeout: null,
      plannerExtended: false,
      isDragging: false,
    };
  },
  computed: {
    ...mapGetters("layers", ["getLayers"]),
    ...mapGetters(["isDashboardMode"]),
    ...mapGetters([
      "showResults",
      "showBatResults",
      "showMhpResults",
      "showSstResults",
    ]),
  },
  watch: {
    isDashboardMode(isDashboard) {
      const mapTarget = isDashboard ? "map" : "large-map";
      clearTimeout(this.setTargetMapTimeout);

      this.setTargetMapTimeout = setTimeout(() => {
        const map = getMap();
        if (map) {
          map.setTarget(null);
          map.setTarget(mapTarget);
        }
        // if we are comming from mep tool to dashboard mode
        // fetch reselect last selected region to fetch infographics data
        isDashboard && getNavigator() && getNavigator().reselectLastRegion();
      }, 100);
    },
  },
  async mounted() {
    const layers = await this.fetchLayers();
    let map = getMap({ check: true });
    if (map == null) {
      map = initMap({ target: this.isDashboardMode ? "map" : "large-map" });
      if (getNavigator() === null) {
        initNavigator(map);
      }
    } else {
      map = initMap({ target: this.isDashboardMode ? "map" : "large-map" });
    }
    layers.forEach((layer) => map.addLayer(layer));
    this.fetchWorkingArea();
    bus.$emit("navigator-initialized", getNavigator());
    getNavigator().country.select(0, { reselect: true });
    setTimeout(fitToNepalBounds, 1000);
  },
  methods: {
    ...mapActions(["toggleMode"]),
    ...mapActions("tools", ["fetchWorkingArea"]),
    ...mapActions("layers", ["fetchLayers", "removeLayers"]),
    ...mapActions("infographics", ["fetchInfographicsData"]),
    press() {
      this.isDragging = true;
    },
    release() {
      this.isDragging = false;
    },
    handleDrag(event) {
      if (!this.isDragging) return;
      const width = event.clientX - this.$refs.toolWrapper.offsetLeft - 3;
      this.$refs.mepToolMapWrapper.style.minWidth = `${width}px`;
      this.$refs.mepToolMapWrapper.style.maxWidth = `${width}px`;
      getMap() && getMap().updateSize();
    },
  },
  beforeDestroy() {
    getMap() && getMap().setTarget(null);
    this.removeLayers();
    if (!this.isDashboardMode) this.toggleMode();
  },
};
</script>

<style lang="scss" scoped>
.home-container {
  display: grid;
  height: 100vh;
  max-height: 1000px;
  grid-template-rows: auto minmax(0, 1fr) auto;
  &.show-results {
    height: calc(100vh - 3.5em);
  }
  &.hideSidebar {
    .main-container {
      grid-template-columns: 1fr;
      .sidebar-wrapper,
      .pseudo-sidebar-before {
        display: none;
      }
    }
  }

  .main-container {
    display: grid;
    grid-template-columns: minmax(200px, 17%) 1fr;
    position: relative;

    .sidebar-wrapper {
      position: absolute;
      // overflow-y: auto;
      padding: 0 5px;
      top: 0;
      bottom: 0;
      width: 17%;
      min-width: 200px;
      background: $secondary-bg;
    }
    .pseudo-sidebar-before {
      z-index: -1;
    }

    .dashboard-tool-wrapper {
      min-height: 0;
      height: 100%;
      padding: 5px;
      overflow: auto;
    }
  }
}

.map-plan-wrapper {
  display: flex;
  height: 100%;
  > div {
    flex: 1;
  }
  .plan-wrapper {
    position: relative;
    margin-left: 8px;
    border-radius: 4px;
    overflow-y: auto;
    overflow-x: hidden;
    height: 100%;
    border: 1px solid lighten($secondary-color, 25%);
    background: darken(white, 2%);
    height: 100%;

    .width-toggler {
      position: -webkit-sticky; /* Safari */
      position: sticky;
      float: left;
      z-index: 10;
      top: 50%;
      transform: translate(30%, -50%);
      width: 15px;
      margin-top: -70px;
      height: 70px;
      border-radius: 60% 10px 10px 60%;
      background: rgba(rgb(139, 139, 139), 0.4);
      cursor: pointer;
      /* box-shadow: 0 0 5px rgba(black, 0.4); */
      @include flexCenter();

      &:before {
        content: "\25C2";
        color: lighten($primary-color, 10%);
        transform: scaleX(0.45) scale(1.3);
      }
    }
  }
}
.top-level-wrapper.grabbing {
  cursor: ew-resize; /* url(../../../assets/icons/resize.svg), auto; */
}
#mep-tool-wrapper {
  position: relative;
  #resizer {
    left: 100%;
    top: 50%;
    transform: translateY(-50%);

    $grabber-color: lighten($primary-color, 10%);
    box-shadow: inset -2px 0px $grabber-color, inset -4px 0px $page-bg,
      inset -6px 0px $grabber-color;
    width: 6px;
    height: 100px;
    position: absolute;
    padding: 4px;
    margin-left: -1px;
    z-index: 100;
    /* background: transparent; */
    cursor: ew-resize; /* url(../../../assets/icons/resize.svg), auto; */
  }
}
.extended {
  #resizer {
    display: none;
  }
  #mep-tool-wrapper {
    max-width: 0 !important;
    min-width: 0 !important;
  }
  #large-map {
    flex: 0;
    opacity: 0;
  }
  .map-plan-wrapper .plan-wrapper .width-toggler {
    transform: translateY(-50%) scaleX(-1);
  }
}

.result-outer-wrapper {
  min-height: 95vh;
  display: grid;
  grid-template-rows: auto 1fr auto;
  background: white;
  .divider {
    height: 0.4em;
    background: $page-bg;
  }
}
</style>
